/**
 * Tucoiva Product Inquiry
 * Standalone Dwell/Horizon-compatible theme block behavior.
 */
(() => {
  const TAG_NAME = 'tucoiva-product-inquiry';

  if (customElements.get(TAG_NAME)) return;

  class TucoivaProductInquiry extends HTMLElement {
    connectedCallback() {
      if (this.dataset.tpiInitialized === 'true') return;
      this.dataset.tpiInitialized = 'true';

      this.dialog = this.querySelector('[data-tpi-dialog]');
      this.openButton = this.querySelector('[data-tpi-open-dialog]');
      this.whatsappButton = this.querySelector('[data-tpi-whatsapp]');
      this.closeButtons = this.querySelectorAll('[data-tpi-close-dialog]');
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('[data-tpi-submit]');
      this.productScope = this.closest('.shopify-section') || document;
      this.variants = this.readVariants();

      this.handleOpen = this.handleOpen.bind(this);
      this.handleWhatsApp = this.handleWhatsApp.bind(this);
      this.handleDialogClick = this.handleDialogClick.bind(this);
      this.handleScopeChange = this.handleScopeChange.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);

      this.openButton?.addEventListener('click', this.handleOpen);
      this.whatsappButton?.addEventListener('click', this.handleWhatsApp);
      this.closeButtons.forEach((button) => {
        button.addEventListener('click', () => this.closeDialog());
      });
      this.dialog?.addEventListener('click', this.handleDialogClick);
      this.productScope?.addEventListener('change', this.handleScopeChange, true);
      this.form?.addEventListener('submit', this.handleSubmit);

      this.initializeCountryField();
      this.updateProductContext();

      const state = this.querySelector('[data-tpi-form-state]');
      if (state?.dataset.openOnLoad === 'true') {
        window.requestAnimationFrame(() => this.openDialog());
      }
    }

    disconnectedCallback() {
      this.openButton?.removeEventListener('click', this.handleOpen);
      this.whatsappButton?.removeEventListener('click', this.handleWhatsApp);
      this.dialog?.removeEventListener('click', this.handleDialogClick);
      this.productScope?.removeEventListener('change', this.handleScopeChange, true);
      this.form?.removeEventListener('submit', this.handleSubmit);
    }

    initializeCountryField() {
      const countrySelect = this.querySelector('[data-tpi-country]');
      if (!countrySelect) return;

      const wasEmpty = !countrySelect.value || countrySelect.value === '---';
      countrySelect.querySelectorAll('option[value="---"]').forEach((option) => option.remove());

      let placeholder = countrySelect.querySelector('option[value=""]');
      if (!placeholder) {
        placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.disabled = true;
        countrySelect.prepend(placeholder);
      }

      placeholder.textContent = countrySelect.dataset.placeholder || 'Select a country or region';

      if (wasEmpty) {
        const defaultCountry = (countrySelect.dataset.defaultCountry || '').trim();
        const hasDefaultCountry = Array.from(countrySelect.options).some(
          (option) => option.value === defaultCountry
        );

        countrySelect.value = hasDefaultCountry ? defaultCountry : '';
      }
    }

    readVariants() {
      const source = this.querySelector('[data-tpi-variants]');
      if (!source) return [];

      try {
        const parsed = JSON.parse(source.textContent || '[]');
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.warn('[Product inquiry] Unable to read product variants.', error);
        return [];
      }
    }

    getSelectedVariantId() {
      const productForms = this.productScope?.querySelectorAll('form[action*="/cart/add"]') || [];

      for (const form of productForms) {
        const fields = form.querySelectorAll('[name="id"]');
        for (const field of fields) {
          const isChoice = field.type === 'radio' || field.type === 'checkbox';
          if (isChoice && !field.checked) continue;
          if (field.value) return String(field.value);
        }
      }

      const urlVariant = new URL(window.location.href).searchParams.get('variant');
      return urlVariant || String(this.dataset.defaultVariantId || '');
    }

    getSelectedVariant() {
      const variantId = this.getSelectedVariantId();
      return this.variants.find((variant) => String(variant.id) === variantId) || this.variants[0] || null;
    }

    buildProductUrl(variant) {
      let url;

      try {
        url = new URL(this.dataset.productUrl || window.location.pathname, window.location.origin);
      } catch (_error) {
        url = new URL(window.location.href);
      }

      if (variant?.id) {
        url.searchParams.set('variant', String(variant.id));
      }

      return url.toString();
    }

    getContext() {
      const variant = this.getSelectedVariant();
      const productTitle = this.dataset.productTitle || document.title;
      const variantTitle = variant?.title && variant.title !== 'Default Title' ? variant.title : '';
      const sku = variant?.sku || '';
      const productUrl = this.buildProductUrl(variant);

      return { variant, productTitle, variantTitle, sku, productUrl };
    }

    updateProductContext() {
      const context = this.getContext();
      const setValue = (key, value) => {
        const input = this.querySelector(`[data-tpi-field="${key}"]`);
        if (input) input.value = value || '';
      };

      setValue('product_title', context.productTitle);
      setValue('product_url', context.productUrl);
      setValue('selected_variant', context.variantTitle || 'Default');
      setValue('variant_sku', context.sku);

      const productSummary = this.querySelector('[data-tpi-summary-product]');
      const variantSummary = this.querySelector('[data-tpi-summary-variant]');
      const variantRow = this.querySelector('[data-tpi-summary-variant-row]');
      const productLink = this.querySelector('[data-tpi-summary-link]');

      if (productSummary) productSummary.textContent = context.productTitle;
      if (variantSummary) variantSummary.textContent = context.variantTitle;
      if (variantRow) variantRow.hidden = !context.variantTitle;
      if (productLink) productLink.href = context.productUrl;

      return context;
    }

    buildWhatsAppMessage(context) {
      const lines = [];
      const intro = (this.dataset.whatsappIntro || '').trim();

      if (intro) lines.push(intro);
      lines.push('', `Product: ${context.productTitle}`);
      if (context.variantTitle) lines.push(`Selected option: ${context.variantTitle}`);
      if (context.sku) lines.push(`SKU: ${context.sku}`);
      lines.push(`Product link: ${context.productUrl}`, '', 'My question:');

      return lines.join('\n');
    }

    handleWhatsApp() {
      const number = (this.dataset.whatsappNumber || '').replace(/\D/g, '');
      if (!number) return;

      const context = this.updateProductContext();
      const message = encodeURIComponent(this.buildWhatsAppMessage(context));
      const url = `https://wa.me/${number}?text=${message}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    }

    handleOpen() {
      this.updateProductContext();
      this.openDialog();
    }

    openDialog() {
      if (!this.dialog) return;

      if (typeof this.dialog.showModal === 'function') {
        if (!this.dialog.open) this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }

      const firstField = this.dialog.querySelector('input:not([type="hidden"]), textarea, button');
      window.requestAnimationFrame(() => firstField?.focus({ preventScroll: true }));
    }

    closeDialog() {
      if (!this.dialog) return;

      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }

      this.openButton?.focus({ preventScroll: true });
    }

    handleDialogClick(event) {
      if (event.target === this.dialog) this.closeDialog();
    }

    handleScopeChange() {
      window.requestAnimationFrame(() => this.updateProductContext());
    }

    handleSubmit() {
      this.updateProductContext();

      if (this.submitButton) {
        this.submitButton.disabled = true;
        this.submitButton.setAttribute('aria-busy', 'true');
      }
    }
  }

  customElements.define(TAG_NAME, TucoivaProductInquiry);
})();
